/* Portal Requests inbox — one page where dispatch reviews everything that
   came in from the customer portal:
     • pending_approval jobs (account customers)
     • pending quote_requests (cash customers)
   Approve → converts / activates. Reject → cancelled with a reason. */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Inbox, CheckCircle2, XCircle, RefreshCcw, User, MapPin, Package, Clock, DollarSign, FileText, Link2, AlertTriangle, Sparkles, Mail, Phone } from "lucide-react";
import { formatLocalDateTime } from "@/lib/dates";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PortalRequests() {
  const [data, setData] = useState({ pending_jobs: [], pending_quotes: [], enquiries: [], counts: { total: 0 } });
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/portal-requests"),
        api.get("/admin/order-tracking-audit").catch(() => ({ data: { drift_count: 0, rows: [] } })),
      ]);
      setData(r.data);
      setAudit(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load portal requests");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [audit, setAudit] = useState({ drift_count: 0, rows: [] });
  const [relinking, setRelinking] = useState(false);

  const relinkAll = async () => {
    if (!window.confirm(`Re-link ${audit.drift_count} order${audit.drift_count === 1 ? "" : "s"}? This will update tracking IDs to match customer order numbers.`)) return;
    setRelinking(true);
    try {
      const r = await api.post("/admin/relink-order-ids", { dry_run: false });
      toast.success(`Relinked ${r.data.relinked_count} order${r.data.relinked_count === 1 ? "" : "s"}`, { duration: 6000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Relink failed");
    } finally { setRelinking(false); }
  };

  const openInDispatch = (jobDate) => nav(`/dispatch?date=${jobDate || ""}`);

  const markContacted = async (l) => {
    try {
      await api.patch(`/leads/${l.id}`, { status: "contacted" });
      toast.success(`Marked contacted · ${l.company || l.name}`, {
        duration: 6000,
        action: { label: "Open Leads", onClick: () => nav("/leads") },
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const approveJob = async (j) => {
    try {
      await api.post(`/portal-requests/jobs/${j.id}/approve`);
      toast.success(`Approved · ${j.customer_name} · ${j.date}`, {
        duration: 8000,
        action: { label: "Open in Dispatch", onClick: () => openInDispatch(j.date) },
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const rejectJob = async (j) => {
    const reason = window.prompt(`Reject this job from ${j.customer_name}? Reason:`, "Not able to fulfil");
    if (!reason) return;
    try { await api.post(`/portal-requests/jobs/${j.id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };
  const approveQuote = async (q) => {
    if (!window.confirm(`Convert ${q.customer_name}'s quote to a scheduled job?`)) return;
    try {
      const r = await api.post(`/portal-requests/quotes/${q.id}/approve`);
      const jobDate = q.requested_date || "";
      toast.success(`Quote approved · job scheduled for ${jobDate}`, {
        duration: 8000,
        action: { label: "Open in Dispatch", onClick: () => openInDispatch(jobDate) },
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const rejectQuote = async (q) => {
    const reason = window.prompt(`Reject ${q.customer_name}'s quote? Reason:`, "Price / date not viable");
    if (!reason) return;
    try { await api.post(`/portal-requests/quotes/${q.id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="portal-requests-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5" /> Portal Inbox</div>
          <h1 className="heading text-3xl font-black uppercase">Portal Requests</h1>
          <p className="text-sm text-muted-industrial mt-1">
            Approve or reject jobs and quote requests that customers have sent through the portal.
          </p>
        </div>
        <button data-testid="portal-req-refresh" onClick={load}
                className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-1.5">
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* v3.6x — Order ↔ Tracking drift audit. Fires when jobs created
          from an OLD approval flow have a job.id ≠ quote.id, causing the
          customer to see one order # while dispatch sees another. */}
      {audit.drift_count > 0 && (
        <div className="border-2 border-amber-600 bg-amber-950/20 p-4" data-testid="order-tracking-drift-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="mono text-[10px] uppercase tracking-widest text-amber-400 font-bold">Order ↔ Tracking Drift</div>
              <div className="text-sm text-ink mt-0.5">
                <b className="text-amber-300">{audit.drift_count}</b> order{audit.drift_count === 1 ? "" : "s"} on {audit.date} have a
                different tracking # than the customer's order #. Re-link now to align them.
              </div>
              <div className="mt-2 max-h-32 overflow-y-auto text-[11px] mono">
                {audit.rows.slice(0, 8).map((r) => (
                  <div key={r.quote_id} className="text-muted-industrial" data-testid={`drift-row-${r.quote_id}`}>
                    <span className="text-amber-300 font-bold">#{r.customer_order_short}</span>
                    <span className="opacity-60"> → tracking #</span>
                    <span className="text-red-400">#{r.job_id_short_now}</span>
                    <span className="opacity-60"> · {r.customer_name} · {r.docket_count} docket{r.docket_count === 1 ? "" : "s"}</span>
                  </div>
                ))}
                {audit.rows.length > 8 && (
                  <div className="opacity-60">+{audit.rows.length - 8} more</div>
                )}
              </div>
              <div className="mt-3">
                <Button
                  onClick={relinkAll}
                  disabled={relinking}
                  data-testid="relink-orders-btn"
                  className="rounded-none bg-amber-600 hover:bg-amber-500 text-black uppercase tracking-widest text-xs font-bold"
                >
                  <Link2 className="w-3.5 h-3.5 mr-1" />
                  {relinking ? "Relinking…" : `Relink ${audit.drift_count} Order${audit.drift_count === 1 ? "" : "s"}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {data.counts.total === 0 && !loading && (
        <div className="card-industrial p-12 text-center" data-testid="portal-req-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <div className="heading text-lg font-bold uppercase">Inbox zero</div>
          <div className="text-sm text-muted-industrial mt-1">Nothing waiting — you're all caught up.</div>
        </div>
      )}

      {/* Pending Jobs (account customers) */}
      {data.pending_jobs.length > 0 && (
        <section className="space-y-3" data-testid="portal-req-jobs-section">
          <div className="flex items-center gap-2">
            <div className="label-tag">Pending Jobs</div>
            <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-primary-orange text-primary-orange">
              {data.pending_jobs.length} · account customers
            </span>
          </div>
          <div className="grid gap-3">
            {data.pending_jobs.map((j) => (
              <div key={j.id} data-testid={`pending-job-${j.id}`} className="card-industrial p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-primary-orange" />
                      <span className="font-bold text-lg">{j.customer_name}</span>
                      <span className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                        · {formatLocalDateTime(j.created_at)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mt-2">
                      <div><div className="label-tag">Date</div><div className="mono">{j.date} · {j.time_required || "no time"}</div></div>
                      <div><div className="label-tag">Mix</div><div className="mono">{j.mix_code}</div></div>
                      <div><div className="label-tag">Quantity</div><div className="mono text-primary-orange font-bold">{j.quantity_m3} m³</div></div>
                      <div><div className="label-tag">Rate</div><div className="mono">{money(j.price_per_m3)}/m³</div></div>
                    </div>
                    <div className="text-xs text-muted-industrial mt-2 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{j.site_name || j.site_address || "Site not set"}
                    </div>
                    {j.notes && <div className="text-xs text-muted-industrial mt-1 italic">"{j.notes}"</div>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button data-testid={`approve-job-${j.id}`} onClick={() => approveJob(j)}
                            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs font-bold">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button data-testid={`reject-job-${j.id}`} onClick={() => rejectJob(j)} variant="outline"
                            className="rounded-none border-industrial hover:border-red-500 hover:text-red-400 uppercase tracking-widest text-xs">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pending Quote Requests (cash customers) */}
      {data.pending_quotes.length > 0 && (
        <section className="space-y-3" data-testid="portal-req-quotes-section">
          <div className="flex items-center gap-2">
            <div className="label-tag">Quote Requests</div>
            <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-sky-500/60 text-sky-400">
              {data.pending_quotes.length} · cash customers
            </span>
          </div>
          <div className="grid gap-3">
            {data.pending_quotes.map((q) => (
              <div key={q.id} data-testid={`pending-quote-${q.id}`} className="card-industrial p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-sky-400" />
                      <span className="font-bold text-lg">{q.customer_name}</span>
                      <span className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                        · {formatLocalDateTime(q.created_at)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mt-2">
                      <div><div className="label-tag">Requested</div><div className="mono">{q.requested_date} · {q.time_required || "no time"}</div></div>
                      <div><div className="label-tag">Mix</div><div className="mono">{q.mix_code}</div></div>
                      <div><div className="label-tag">Quantity</div><div className="mono text-primary-orange font-bold">{q.quantity_m3} m³</div></div>
                      <div><div className="label-tag">Est. Total</div><div className="mono text-primary-orange font-bold">{money(q.total)}</div></div>
                    </div>
                    <div className="text-xs text-muted-industrial mt-2 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{q.site_name || q.site_address || "Site not set"}
                    </div>
                    {q.notes && <div className="text-xs text-muted-industrial mt-1 italic">"{q.notes}"</div>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button data-testid={`approve-quote-${q.id}`} onClick={() => approveQuote(q)}
                            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs font-bold">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve → Job
                    </Button>
                    <Button data-testid={`reject-quote-${q.id}`} onClick={() => rejectQuote(q)} variant="outline"
                            className="rounded-none border-industrial hover:border-red-500 hover:text-red-400 uppercase tracking-widest text-xs">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Website & demo enquiries (from the public site / vault / feature showcase) */}
      {(data.enquiries?.length > 0) && (
        <section className="space-y-3" data-testid="portal-req-enquiries-section">
          <div className="flex items-center gap-2">
            <div className="label-tag">Enquiries</div>
            <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-emerald-500/60 text-emerald-400">
              {data.enquiries.length} · website & demo requests
            </span>
          </div>
          <div className="grid gap-3">
            {data.enquiries.map((l) => {
              const kind = l.source === "demo_request" ? "Demo request" : "Website enquiry";
              return (
                <div key={l.id} data-testid={`enquiry-${l.id}`} className="card-industrial p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Sparkles className="w-4 h-4 text-emerald-400" />
                        <span className="font-bold text-lg">{l.company || l.name}</span>
                        <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500/50 text-emerald-400">{kind}</span>
                        <span className="mono text-[10px] uppercase tracking-widest text-muted-industrial">· {formatLocalDateTime(l.created_at)}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm mt-2">
                        {l.name && <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-muted-industrial" /><span className="mono">{l.name}</span></div>}
                        {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-primary-orange"><Mail className="w-3.5 h-3.5 text-muted-industrial" /><span className="mono">{l.email}</span></a>}
                        {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 hover:text-primary-orange"><Phone className="w-3.5 h-3.5 text-muted-industrial" /><span className="mono">{l.phone}</span></a>}
                      </div>
                      {l.message && <div className="text-sm text-muted-industrial mt-2 italic border-l-2 border-industrial pl-2.5">"{l.message}"</div>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Button data-testid={`enquiry-contacted-${l.id}`} onClick={() => markContacted(l)}
                              className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-black uppercase tracking-widest text-xs font-bold">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Contacted
                      </Button>
                      <Button data-testid={`enquiry-open-leads-${l.id}`} onClick={() => nav("/leads")} variant="outline"
                              className="rounded-none border-industrial hover:border-primary-orange hover:text-primary-orange uppercase tracking-widest text-xs">
                        Open in Leads
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
